import { EN_EAGER_BUNDLE } from '@/lib/i18n/bundles/en-eager';
import type { Bundle, LazyNamespace, Namespace } from '@/lib/i18n/resources';
import { UiLanguage } from '@/types/generated/enums';

// English carries only its eager namespaces; route loaders fetch the rest via loadEnglishNamespace.
type LanguageBundle = Partial<Record<Namespace, Bundle>>;

// Adding a backend language breaks the build here until it has an entry. Each
// non-default entry is its own dynamic import, so Vite splits it into its own chunk
// and a session that never switches to it never downloads that chunk.
const LOADERS: Record<UiLanguage, () => Promise<LanguageBundle>> = {
  [UiLanguage.ENGLISH]: () => Promise.resolve(EN_EAGER_BUNDLE),
  [UiLanguage.SPANISH]: () =>
    import('@/lib/i18n/bundles/es').then((module) => module.ES_BUNDLE),
  [UiLanguage.GREEK]: () =>
    import('@/lib/i18n/bundles/el').then((module) => module.EL_BUNDLE),
};

export const loadLanguageBundle = (
  language: UiLanguage,
): Promise<LanguageBundle> => LOADERS[language]();

// One dynamic import each, so each namespace is its own chunk; a new namespace fails the build here.
const EN_NAMESPACE_LOADERS: Record<
  LazyNamespace,
  () => Promise<{ default: Bundle }>
> = {
  auth: () => import('@/locales/en/auth.json'),
  settings: () => import('@/locales/en/settings.json'),
  dashboard: () => import('@/locales/en/dashboard.json'),
  reports: () => import('@/locales/en/reports.json'),
  copilot: () => import('@/locales/en/copilot.json'),
  audiences: () => import('@/locales/en/audiences.json'),
  onboarding: () => import('@/locales/en/onboarding.json'),
  assets: () => import('@/locales/en/assets.json'),
  billing: () => import('@/locales/en/billing.json'),
  goals: () => import('@/locales/en/goals.json'),
  insights: () => import('@/locales/en/insights.json'),
  strategy: () => import('@/locales/en/strategy.json'),
  wizard: () => import('@/locales/en/wizard.json'),
  support: () => import('@/locales/en/support.json'),
  landing: () => import('@/locales/en/landing.json'),
  agency: () => import('@/locales/en/agency.json'),
  tour: () => import('@/locales/en/tour.json'),
};

export const loadEnglishNamespace = (
  namespace: LazyNamespace,
): Promise<Bundle> =>
  EN_NAMESPACE_LOADERS[namespace]().then((module) => module.default);
